import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import { withRouter } from "react-router-dom"
import { Link } from "react-router-dom"


import menu from "common/data/sideMenu"
import AccessControlWrapper from "components/Common/PermissionButton"
import { mergeMenuEntries } from "helpers/menu_merge_helper"
import FullPageLoader from "components/Common/FullPageLoader"

const SidebarContent = props => {
    const [userAppMenu, setUserAppMenu] = useState(menu)
    const [isRedirecting, setIsRedirecting] = useState(true)
    const { appInfo, userMenu } = props

    // Pages that should be excluded from redirect logic (like profile, auth pages, etc.)
    const excludedPages = [
        '/profile',
        '/user-profile',
        '/pages-login',
        '/pages-register',
        '/logout',
        '/auth-',
        '/page-'
    ]

    useEffect(() => {
        if (userMenu?.length > 0) {
            const finalAppMenu = mergeMenus(menu, userMenu)
            // const finalUserMenu = mergeMenus(finalAppMenu, userMenu)
            setUserAppMenu(finalAppMenu)
        }
    }, [userMenu])

    // Check if current path should be redirected
    useEffect(() => {
        const currentPath = props.location.pathname

        // Don't redirect if userAppMenu is empty or still loading
        if (!userAppMenu || userAppMenu.length === 0) return

        // Check if current path is in the menu list (including nested menus)
        const isValidMenuPath = userAppMenu.some(menuItem => {
            // Check main menu item
            if (currentPath === menuItem.href ||
                currentPath.startsWith(menuItem.href + '/') ||
                (menuItem.href === '/dashboard' && currentPath === '/')) {
                return true
            }
            // Check children if it's a parent menu
            if (menuItem.children) {
                return menuItem.children.some(childItem =>
                    currentPath === childItem.href ||
                    currentPath.startsWith(childItem.href + '/') ||
                    (childItem.href === '/dashboard' && currentPath === '/')
                )
            }
            return false
        })

        // Check if current path should be excluded from redirect
        const isExcludedPath = excludedPages.some(excludedPath =>
            currentPath.includes(excludedPath)
        )

        // If current path is not in menu and not excluded, redirect to first available menu item
        if (!isValidMenuPath && !isExcludedPath && userAppMenu.length > 0) {
            let firstMenuItem = null
            
            // Find first accessible menu item (could be top-level or child)
            for (const menuItem of userAppMenu) {
                if (menuItem.href && menuItem.href !== '#' && !menuItem.isParent) {
                    firstMenuItem = menuItem
                    break
                } else if (menuItem.children && menuItem.children.length > 0) {
                    // If it's a parent, get first child
                    firstMenuItem = menuItem.children[0]
                    break
                }
            }
            
            if (firstMenuItem && firstMenuItem.href) {
                // Add a small delay to show the loader, then redirect
                setTimeout(() => {
                    props.history.replace(firstMenuItem.href)
                    setIsRedirecting(false)
                }, 300)
            }
        } else {
            setIsRedirecting(false)
        }
    }, [props.location.pathname, userAppMenu, props.history])

    function mergeMenus(frontendMenus, backendMenus) {
        return processMenuGroups(mergeMenuEntries(frontendMenus, backendMenus))
    }

    function processMenuGroups(menuItems) {
        const result = []
        const groupedItems = {}
        const groupFirstIndex = {} // Track first appearance of each group

        // First pass: collect all items and track group positions
        
        menuItems.forEach(item => {
            if (!item.menuGroup) {
                // Top-level item - keep in original position
                result[item.originalIndex] = item
            } else {
                // Child item - group by menuGroup
                if (!groupedItems[item.menuGroup]) {
                    groupedItems[item.menuGroup] = []
                    groupFirstIndex[item.menuGroup] = item.originalIndex
                }
                groupedItems[item.menuGroup].push(item)
            }
        })

        // Second pass: create parent menu items for each group at the position of first child
        Object.keys(groupedItems).forEach(groupName => {
            const children = groupedItems[groupName]
            // Sort children by their original index
            children.sort((a, b) => a.originalIndex - b.originalIndex)
            
            // Choose appropriate icon based on group name
            let groupIcon = <i className="bx bx-folder-open"></i> // default
            if (groupName.toLowerCase().includes('transaction')) {
                groupIcon = <i className="bx bx-transfer-alt"></i>
            } else if (groupName.toLowerCase().includes('reconcil')) {
                groupIcon = <i className="bx bx-check-double"></i>
            } else if (groupName.toLowerCase().includes('cashback')) {
                groupIcon = <i className="bx bx-time-five"></i>
            } else if (groupName.toLowerCase().includes('banking')) {
                groupIcon = <i className="bx bx-credit-card"></i>
            }
            
            const parentItem = {
                name: groupName,
                href: "#",
                contextUri: "",
                actionType: "",
                icon: groupIcon,
                isParent: true,
                children: children,
                originalIndex: groupFirstIndex[groupName]
            }
            result[groupFirstIndex[groupName]] = parentItem
        })

        // Filter out undefined slots and return sorted array
        return result.filter(item => item).sort((a, b) => a.originalIndex - b.originalIndex)
    }

    const ref = useRef()
    const metisMenuRef = useRef(null)

    // Initialize MetisMenu only once
    useEffect(() => {
        if (userAppMenu && userAppMenu.length > 0) {
            // Clean up existing MetisMenu instance
            if (metisMenuRef.current) {
                metisMenuRef.current.dispose()
            }
            
            // Initialize new MetisMenu
            metisMenuRef.current = new MetisMenu("#side-menu", { toggle: false })
        }
        
        // Cleanup on unmount
        return () => {
            if (metisMenuRef.current) {
                metisMenuRef.current.dispose()
            }
        }
    }, [userAppMenu])

    // Handle active menu highlighting on route changes
    useEffect(() => {
        if (!userAppMenu || userAppMenu.length === 0) return

        const currentPath = props.location.pathname
        const ul = document.getElementById("side-menu")

        if (ul) {
            // Remove all existing active classes
            const allLinks = ul.querySelectorAll("a")
            allLinks.forEach(link => link.classList.remove("active"))

            // Reset only leaf items. A group <li>'s mm-active is owned by MetisMenu:
            // its toggle() reads it to decide open vs close, so clearing it here made
            // open groups ignore the next click. Groups only change on header click.
            ul.querySelectorAll("li").forEach(li => {
                if (!li.querySelector("ul")) li.classList.remove("mm-active")
            })

            const menuPaths = []
            userAppMenu.forEach(item => {
                if (item.href && item.href !== '#') menuPaths.push(item.href)
                if (item.children) {
                    item.children.forEach(child => {
                        if (child.href && child.href !== '#') menuPaths.push(child.href)
                    })
                }
            })

            let bestMatch = null
            let bestMatchLength = 0

            for (const menuPath of menuPaths) {
                if (currentPath === menuPath && menuPath.length > bestMatchLength) {
                    bestMatch = menuPath
                    bestMatchLength = menuPath.length
                }
                else if (currentPath.startsWith(menuPath + '/') && menuPath.length > bestMatchLength) {
                    bestMatch = menuPath
                    bestMatchLength = menuPath.length
                }
            }

            // Special case: if we're at root '/', match dashboard
            if (!bestMatch && currentPath === '/') {
                bestMatch = '/dashboard'
            }

            // Find and activate the DOM element that matches bestMatch
            if (bestMatch) {
                const items = ul.getElementsByTagName("a")
                for (let i = 0; i < items.length; ++i) {
                    const itemPath = items[i].getAttribute('href') || items[i].pathname
                    if (itemPath === bestMatch) {
                        activateParentDropdown(items[i])
                        break
                    }
                }
            }
        }
    }, [props.location.pathname, userAppMenu])

    useEffect(() => {
        ref.current.recalculate()
    })

    function scrollElement(item) {
        if (item) {
            const currentPosition = item.offsetTop
            if (currentPosition > window.innerHeight) {
                ref.current.getScrollElement().scrollTop = currentPosition - 300
            }
        }
    }

    function activateParentDropdown(item) {
        item.classList.add("active")
        const currentLi = item.closest('li')
        
        if (currentLi) {
            currentLi.classList.add("mm-active")
            
            // Check if this item is inside a sub-menu (nested structure)
            const parentUl = currentLi.closest('ul.sub-menu')
            if (parentUl) {
                // This is a child item, find and activate the parent menu
                const parentLi = parentUl.closest('li')
                if (parentLi) {
                    const parentMenuLink = parentLi.querySelector('.has-arrow')
                    if (parentMenuLink) {
                        parentMenuLink.classList.add("active")
                        // Use MetisMenu's own show() so its internal open/closed state
                        // (tracked on the <ul>, not the <li>) stays in sync with
                        // route-driven activation. Manually setting classes here used to
                        // desync the library, so a later click to toggle this section
                        // would silently do nothing. (Sudip, 2026-09-23)
                        if (metisMenuRef.current && !parentUl.classList.contains('mm-show')) {
                            metisMenuRef.current.show(parentUl)
                        }
                    }
                }
            }
            
            scrollElement(item)
        }
        return false
    }

    return (
        <React.Fragment>
            <SimpleBar className="h-100" ref={ref}>
                <div id="sidebar-menu">
                    <ul className="metismenu list-unstyled" id="side-menu">
                        {userAppMenu?.map((menuItem, index) => {
                            return (
                                <li key={`${menuItem.isParent ? "group" : "item"}-${menuItem.name}-${index}`} data-title={menuItem.name}>
                                    {menuItem.isParent ? (
                                        // Parent menu with children
                                        <>
                                            <a href="#" className="has-arrow" title={menuItem.name}>
                                                {menuItem.icon}
                                                <span>{menuItem.name}</span>
                                            </a>
                                            <ul className="sub-menu">
                                                {menuItem.children?.map((childItem, childIndex) => (
                                                    <li key={childIndex} data-title={childItem.name}>
                                                        {childItem.contextUri && childItem.actionType ? (
                                                            <AccessControlWrapper
                                                                actionType={childItem.actionType}
                                                                contextUri={childItem.contextUri}
                                                            >
                                                                <Link to={childItem.href} title={childItem.name}>
                                                                    {/* {childItem.icon} */}
                                                                    <span>{childItem.name}</span>
                                                                </Link>
                                                            </AccessControlWrapper>
                                                        ) : (
                                                            <Link to={childItem.href} title={childItem.name}>
                                                                {/* {childItem.icon} */}
                                                                <span>{childItem.name}</span>
                                                            </Link>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    ) : (
                                        // Regular menu item
                                        menuItem.contextUri && menuItem.actionType ? (
                                            <AccessControlWrapper
                                                actionType={menuItem.actionType}
                                                contextUri={menuItem.contextUri}
                                            >
                                                <Link to={menuItem.href} title={menuItem.name}>
                                                    {menuItem.icon}
                                                    <span>{menuItem.name}</span>
                                                </Link>
                                            </AccessControlWrapper>
                                        ) : (
                                            <Link to={menuItem.href} title={menuItem.name}>
                                                {menuItem.icon}
                                                <span>{menuItem.name}</span>
                                            </Link>
                                        )
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </SimpleBar>
            <FullPageLoader show={isRedirecting} message="" />
        </React.Fragment>
    )
}

SidebarContent.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
}
export default withRouter((SidebarContent))
